/**
 * Roman numeral / harmonic analysis utilities.
 *
 * Converts a chord name to its Roman numeral relative to a key.
 * e.g. in G major: D → V, Bm → iii, Em → vi
 */

import { Note, Chord as TonalChord } from 'tonal';

const ROMAN = ['I', 'II', 'III', 'IV', 'V', 'VI', 'VII'];
const MAJOR_SCALE_PCS = [0, 2, 4, 5, 7, 9, 11];
const MINOR_SCALE_PCS = [0, 2, 3, 5, 7, 8, 10]; // natural minor

function noteToPC(note) {
  const n = Note.get(note);
  return n.empty ? -1 : (Note.midi(note + '4') ?? -1) % 12;
}

function parseChordRootAndQuality(chordName) {
  if (!chordName) return null;
  const base = String(chordName).replace(/\/.*$/, '').replace(/♯/g, '#').replace(/♭/g, 'b').trim();
  const m = base.match(/^([A-G](?:#|b)?)(.*)$/);
  if (!m) return null;
  const tonic = m[1];
  const suffix = m[2] || '';
  const ls = suffix.toLowerCase();
  const isMinor = /(^|[^a-z])m(?!aj)/i.test(suffix) || /min/.test(ls);
  const isDim = /dim|o/.test(ls);
  return { tonic, isMinor, isDim };
}

function keyScalePCs(key) {
  if (!key) return null;
  const [tonic, mode] = key.trim().split(/\s+/);
  const tonicPC = noteToPC(tonic);
  if (tonicPC < 0) return null;
  const template = mode === 'minor' ? MINOR_SCALE_PCS : MAJOR_SCALE_PCS;
  return template.map(d => (tonicPC + d) % 12);
}

/**
 * Return the Roman numeral label for a chord in a given key.
 *
 * @param {string} chordName  e.g. "Bm", "F#", "C/E"
 * @param {string} key        e.g. "G major"
 * @returns {string|null}     e.g. "iii", "V", "♭VII"
 */
export function chordToRoman(chordName, key) {
  if (!chordName || !key) return null;
  try {
    let parsed = TonalChord.get(chordName.replace(/\/.*$/, ''));
    if (!parsed.tonic) {
      const fallback = parseChordRootAndQuality(chordName);
      if (!fallback) return null;
      parsed = {
        tonic: fallback.tonic,
        quality: fallback.isDim ? 'Diminished' : (fallback.isMinor ? 'Minor' : 'Major'),
      };
    }

    const chordRootPC = noteToPC(parsed.tonic);
    if (chordRootPC < 0) return null;

    const isMinorChord = parsed.quality === 'Minor';
    const isDimChord   = parsed.quality === 'Diminished';

    const scalePCs = keyScalePCs(key);
    if (!scalePCs) return null;

    const degreeIdx = scalePCs.indexOf(chordRootPC);

    if (degreeIdx >= 0) {
      let numeral = ROMAN[degreeIdx];
      if (isMinorChord || isDimChord) numeral = numeral.toLowerCase();
      if (isDimChord) numeral += '°';
      return numeral;
    }

    // Chromatic — label flattened/raised degrees
    const tonicPC = noteToPC(key.trim().split(/\s+/)[0]);
    const interval = (chordRootPC - tonicPC + 12) % 12;
    const chromatic = {
      1: '♭II', 2: 'II', 3: '♭III', 4: 'III',
      6: '♭V',  8: '♭VI', 9: 'VI', 10: '♭VII', 11: 'VII',
    };
    const label = chromatic[interval];
    if (label) return (isMinorChord ? label.toLowerCase() : label);
    return null;
  } catch {
    return null;
  }
}

/**
 * Annotate an entire alignment array with Roman numerals.
 *
 * @param {Array}    alignment        array of line objects with .chords map
 * @param {string}   key
 * @param {number}   [transposition]
 * @param {Function} transposeChordFn (chord, semitones, pref) => string
 * @returns {Map}    Map of `${li}-${wi}` => romanNumeral string
 */
export function buildRomanNumeralMap(alignment, key, transposition, transposeChordFn) {
  const map = new Map();
  if (!alignment || !key) return map;

  alignment.forEach((lineObj, li) => {
    const chords = lineObj.chords || {};
    Object.entries(chords).forEach(([wi, rawChord]) => {
      if (!rawChord) return;
      let displayed = rawChord;
      if (transposition && transposeChordFn) {
        displayed = transposeChordFn(rawChord, transposition, null);
      }
      const roman = chordToRoman(displayed, key);
      if (roman) map.set(`${li}-${wi}`, roman);
    });
  });
  return map;
}
